/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProviderForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing provider form state and validation.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import type { AiProviderForm } from './useProviderData';
import { sanitizeForm } from './useProviderData';

export function useProviderForm(
  formData: AiProviderForm,
  baseline: AiProviderForm | null,
  mode: 'create' | 'edit',
  setFormData: (data: AiProviderForm | ((prev: AiProviderForm) => AiProviderForm)) => void
) {
  const { t } = useI18n();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Helper to extract only relevant fields for comparison
  const getComparableFields = useCallback((form: AiProviderForm) => {
    const sanitized = sanitizeForm(form);
    return {
      provider: sanitized.provider,
      name: sanitized.name,
      apiKey: sanitized.apiKey,
      baseUrl: sanitized.baseUrl,
      organization: sanitized.organization,
      azureApiKeyType: sanitized.azureApiKeyType,
      status: sanitized.status
    };
  }, []);

  const hasChanges = useMemo(() => {
    const current = getComparableFields(formData);
    if (!baseline) {
      const defaults = getComparableFields({
        domainId: 1,
        provider: '',
        name: '',
        apiKey: '',
        baseUrl: '',
        organization: '',
        azureApiKeyType: '',
        capabilities: [],
        status: true
      });
      return JSON.stringify(current) !== JSON.stringify(defaults);
    }
    return JSON.stringify(current) !== JSON.stringify(getComparableFields(baseline));
  }, [baseline, formData, getComparableFields]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!formData.provider) {
      nextErrors.provider = t('aiProviders.validation.providerRequired');
    }

    if (!formData.name) {
      nextErrors.name = t('aiProviders.validation.nameRequired');
    } else if (formData.name.length < 3 || formData.name.length > 100) {
      nextErrors.name = t('aiProviders.validation.nameLength');
    }

    if (!formData.apiKey) {
      nextErrors.apiKey = t('aiProviders.validation.apiKeyRequired');
    } else if (formData.apiKey.length < 10) {
      nextErrors.apiKey = t('aiProviders.validation.apiKeyLength');
    }

    if (formData.baseUrl) {
      try {
        new URL(formData.baseUrl);
      } catch {
        nextErrors.baseUrl = t('aiProviders.validation.baseUrlFormat');
      }
    }

    if (formData.organization && formData.organization.length > 50) {
      nextErrors.organization = t('aiProviders.validation.organizationLength');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  // Handle input change
  const handleInputChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field: keyof AiProviderForm, value: any) => {
      setFormData(prev => {
        const next: AiProviderForm = sanitizeForm({
          ...prev,
          [field]: typeof value === 'string' ? value : value ?? prev[field]
        });

        if (field === 'provider') {
          if (!prev.id) {
            next.baseUrl = value === 'openai' ? 'https://api.openai.com/v1' :
              value === 'google' ? 'https://generativelanguage.googleapis.com/v1beta' : '';
            next.azureApiKeyType = value === 'google' ? 'gemini' : '';
          }
        }

        if (field === 'azureApiKeyType' && prev.id === undefined) {
          // Azure defaults
          if (prev.provider === 'azure') {
            if (value === 'openai') {
              next.baseUrl = 'https://YOUR-RESOURCE.openai.azure.com/openai/deployments';
            }
            if (value === 'speech') {
              next.baseUrl = 'https://eastus.tts.speech.microsoft.com/cognitiveservices';
            }
          }
          // Google defaults
          if (prev.provider === 'google') {
            if (value === 'gemini') {
              next.baseUrl = 'https://generativelanguage.googleapis.com/v1beta';
            }
            if (value === 'speech') {
              next.baseUrl = 'https://texttospeech.googleapis.com/v1';
            }
          }
        }

        return next;
      });

      if (errors[field as string]) {
        setErrors(prevErrors => {
          const draft = { ...prevErrors };
          delete draft[field as string];
          return draft;
        });
      }
    },
    [errors, setFormData]
  );

  // Get input classes helper
  const inputClasses = useCallback(
    (hasError?: boolean) =>
      `w-full px-3 py-2 text-sm border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${hasError ? 'border-red-500' : 'border-border'
      }`,
    []
  );

  return {
    errors,
    hasChanges,
    validateForm,
    handleInputChange,
    inputClasses
  };
}

